import type { PrimaryFilterId } from '../../filters/types/filters';
import { isSelectableChartType } from '../ChartConfig/utils/is-selectable-chart-type';
import { getAvailableMetrics } from '../ChartConfig/utils/metric-mapping';
import { getAvailableSegment } from '../ChartConfig/utils/segment-mapping';
import {
  DEFAULT_GRID_COLUMNS,
  DEFAULT_GRID_ROWS,
} from '../PageConfigProvider/constants';
import type { TabContent } from '../PageConfigProvider/page-reducer';
import type {
  ChartType,
  SelectableChartType,
} from '../PageConfigProvider/types/chart';
import type {
  SecondaryDimensionOption,
  SecondaryDimensionValues,
} from '../PageConfigProvider/types/dimension';
import type {
  DimensionSource,
  PlotFilterType,
  PlotOptions,
  PositionedItem,
} from '../PageConfigProvider/types/item';
import type { MetricValues } from '../PageConfigProvider/types/metric';

export interface ReportIntent {
  summary: string;
  title?: string;
  widgets: unknown[];
  secondaryFilters: string[];
}

export interface ReportLLMPositionedItem {
  title: string | null;
  position: { x: number; y: number; width: number; height: number };
  plot?: {
    chartType: string;
    defaultMetricOptions?: string[];
    secondaryDimension?: string;
    plotFilters?: Array<{ type: string; value: number }>;
    dimensionSource?: {
      type: 'PRIMARY_DIMENSION' | 'TOP_SEGMENTS';
      segment?: SecondaryDimensionValues;
    };
    color?: string;
    showDataLabels?: boolean;
    showZebraStripe?: boolean;
    textHtml?: string;
    textPrompt?: string;
    contentSize?: 'small' | 'medium' | 'large' | 'fullPage';
    textVAlign?: 'start' | 'center' | 'end';
    excludeFromExecutiveSummary?: boolean;
  };
}

export interface ReportLLMTabContent {
  items: ReportLLMPositionedItem[];
  grid: { rows: number; columns: number };
  secondaryFilters: { id: string }[];
  textAssistContext?: {
    metrics: Array<{ value: string; description?: string }>;
    filters: Array<{ id: string; label?: string }>;
  };
}

/** Offset from AI coordinate system (y=0) to frontend page 2 (y=36). */
const REPORT_CONTENT_Y_OFFSET = DEFAULT_GRID_ROWS;

/** Minimum report grid rows (title + content + footer). */
const MIN_REPORT_GRID_ROWS = DEFAULT_GRID_ROWS * 3;

export function convertReportTabContentToLLM(
  tabContent: TabContent
): ReportLLMTabContent {
  const contentItems = tabContent.items ?? [];
  return {
    items: contentItems.map((item) => {
      const plot = item.plot;
      let plotPayload: ReportLLMPositionedItem['plot'];
      if (plot) {
        if (plot.chartType === 'text') {
          plotPayload = {
            chartType: 'text',
            textHtml: plot.textHtml ?? '',
            ...(plot.textPrompt && { textPrompt: plot.textPrompt }),
            ...(plot.contentSize && { contentSize: plot.contentSize }),
            ...(plot.textVAlign && { textVAlign: plot.textVAlign }),
            ...(plot.excludeFromExecutiveSummary !== undefined && {
              excludeFromExecutiveSummary: plot.excludeFromExecutiveSummary,
            }),
          };
        } else if (plot.chartType === 'ai-summary') {
          plotPayload = { chartType: 'ai-summary' };
        } else {
          plotPayload = {
            chartType: plot.chartType ?? 'bar',
            defaultMetricOptions: plot.defaultMetricOptions ?? [],
            ...(plot.secondaryDimension && {
              secondaryDimension: plot.secondaryDimension,
            }),
            ...(plot.plotFilters &&
              plot.plotFilters.length > 0 && {
                plotFilters: plot.plotFilters.map((f) => ({
                  type: f.type,
                  value: f.value,
                })),
              }),
            ...(plot.dimensionSource && {
              dimensionSource:
                plot.dimensionSource === 'primary'
                  ? { type: 'PRIMARY_DIMENSION' as const }
                  : {
                      type: 'TOP_SEGMENTS' as const,
                      segment: plot.dimensionSource,
                    },
            }),
            ...(plot.color && { color: plot.color }),
            ...(plot.showDataLabels !== undefined && {
              showDataLabels: plot.showDataLabels,
            }),
            ...(plot.showZebraStripe !== undefined && {
              showZebraStripe: plot.showZebraStripe,
            }),
          };
        }
      }
      return {
        title: item.title,
        position: {
          ...item.position,
          y: Math.max(0, item.position.y - REPORT_CONTENT_Y_OFFSET),
        },
        ...(plotPayload && { plot: plotPayload }),
      };
    }),
    grid: {
      rows: tabContent.grid?.rows ?? DEFAULT_GRID_ROWS * 3,
      columns: DEFAULT_GRID_COLUMNS,
    },
    secondaryFilters: (tabContent.secondaryFilters ?? []).map((f) => ({
      id: f.id,
    })),
  };
}

/** Shift AI content (y=0-based) to page 2 on the frontend (y=36+). */
export function offsetToPage2(items: PositionedItem[]): PositionedItem[] {
  return items.map((item) => ({
    ...item,
    position: {
      ...item.position,
      y: item.position.y + REPORT_CONTENT_Y_OFFSET,
    },
  }));
}

/** Ensure content items are on page 2+ (after the cover page). */
export function ensureContentOnPage2AndBeyond(
  items: PositionedItem[]
): PositionedItem[] {
  return items.map((item) => ({
    ...item,
    position: {
      ...item.position,
      y: Math.max(DEFAULT_GRID_ROWS, item.position.y),
    },
  }));
}

export function requiredGridRowsForItems(items: PositionedItem[]): number {
  if (items.length === 0) return MIN_REPORT_GRID_ROWS;
  const maxExtent = Math.max(
    ...items.map((i) => i.position.y + i.position.height)
  );
  const contentRows =
    Math.ceil(maxExtent / DEFAULT_GRID_ROWS) * DEFAULT_GRID_ROWS;
  return Math.max(MIN_REPORT_GRID_ROWS, contentRows + DEFAULT_GRID_ROWS);
}

function validateAndCorrectDataPlot(
  plot: ReportLLMPositionedItem['plot'],
  primaryFilterId: PrimaryFilterId
): PlotOptions | undefined {
  if (!plot || !plot.defaultMetricOptions) return undefined;

  let chartType = plot.chartType as ChartType;
  const requestedMetrics = plot.defaultMetricOptions as MetricValues[];
  const requestedSecondaryDimension = plot.secondaryDimension as
    | SecondaryDimensionValues
    | undefined;

  if (!isSelectableChartType(chartType)) {
    chartType = 'bar';
  }

  const availableMetricGroups = getAvailableMetrics({
    primaryFilterId,
    chartType: chartType as SelectableChartType,
  });
  const availableMetrics = availableMetricGroups.flatMap((g) =>
    g.options.map((o) => o.value)
  );

  let validMetrics = requestedMetrics.filter((m) =>
    availableMetrics.includes(m)
  );

  if (validMetrics.length === 0 && availableMetrics.length > 0) {
    const firstMetric = availableMetrics[0];
    if (firstMetric) {
      validMetrics = [firstMetric];
    } else {
      return undefined;
    }
  }

  let secondaryDimension = requestedSecondaryDimension;
  const chartTypesWithSecondaryDimension: ChartType[] = [
    'grouped-bar',
    'stacked-bar',
    'box',
    'treemap',
  ];
  const shouldHaveSecondaryDimension =
    chartTypesWithSecondaryDimension.includes(chartType);

  if (secondaryDimension && shouldHaveSecondaryDimension) {
    const primaryMetric = validMetrics[0];
    if (primaryMetric) {
      const availableSegments = getAvailableSegment({
        chartType,
        primaryFilterId,
        metric: primaryMetric,
      });
      const availableSegmentValues = availableSegments.map(
        (s: SecondaryDimensionOption) => s.value
      );
      if (!availableSegmentValues.includes(secondaryDimension)) {
        secondaryDimension =
          (availableSegmentValues[0] as SecondaryDimensionValues) ?? undefined;
      }
    }
  } else if (secondaryDimension && !shouldHaveSecondaryDimension) {
    secondaryDimension = undefined;
  }

  if (!secondaryDimension && shouldHaveSecondaryDimension) {
    const primaryMetric = validMetrics[0];
    if (primaryMetric) {
      const availableSegments = getAvailableSegment({
        chartType,
        primaryFilterId,
        metric: primaryMetric,
      });
      if (availableSegments.length > 0) {
        const firstSegment = availableSegments[0];
        if (firstSegment) {
          secondaryDimension = firstSegment.value;
        }
      }
    }
  }

  let dimensionSource: DimensionSource | undefined;
  if (plot.dimensionSource) {
    if (plot.dimensionSource.type === 'PRIMARY_DIMENSION') {
      dimensionSource = 'primary';
    } else if (
      plot.dimensionSource.type === 'TOP_SEGMENTS' &&
      plot.dimensionSource.segment
    ) {
      dimensionSource = plot.dimensionSource.segment;
    }
  }

  return {
    chartType,
    defaultMetricOptions: validMetrics,
    secondaryDimension,
    ...(dimensionSource && { dimensionSource }),
    ...(plot.plotFilters &&
      plot.plotFilters.length > 0 && {
        plotFilters: plot.plotFilters.map((f) => ({
          type: f.type as PlotFilterType,
          value: f.value,
        })),
      }),
    ...(plot.color && { color: plot.color }),
    ...(plot.showDataLabels !== undefined && {
      showDataLabels: plot.showDataLabels,
    }),
    ...(plot.showZebraStripe !== undefined && {
      showZebraStripe: plot.showZebraStripe,
    }),
  };
}

export function convertReportLLMItemToPositionedItem(
  item: ReportLLMPositionedItem,
  primaryFilterId: PrimaryFilterId
): PositionedItem | null {
  if (!item.plot) {
    return null;
  }

  const chartType = item.plot.chartType;

  if (chartType === 'text') {
    return {
      id: crypto.randomUUID(),
      title: item.title,
      position: item.position,
      plot: {
        chartType: 'text',
        textHtml: item.plot.textHtml ?? '',
        ...(item.plot.textPrompt && { textPrompt: item.plot.textPrompt }),
        ...(item.plot.contentSize && { contentSize: item.plot.contentSize }),
        ...(item.plot.textVAlign && { textVAlign: item.plot.textVAlign }),
        ...(item.plot.excludeFromExecutiveSummary !== undefined && {
          excludeFromExecutiveSummary: item.plot.excludeFromExecutiveSummary,
        }),
      },
    };
  }

  if (chartType === 'ai-summary') {
    return {
      id: crypto.randomUUID(),
      title: item.title,
      position: item.position,
      plot: {
        chartType: 'ai-summary',
      },
    };
  }

  const plot = validateAndCorrectDataPlot(item.plot, primaryFilterId);
  if (!plot) return null;

  return {
    id: crypto.randomUUID(),
    title: item.title,
    position: item.position,
    plot,
  };
}

export function applyConfigToLayout(
  llmItems: ReportLLMPositionedItem[],
  primaryFilterId: PrimaryFilterId
): {
  items: PositionedItem[];
  gridRows: number;
  llmIndexToPositionedId: Map<number, string>;
} {
  const llmIndexToPositionedId = new Map<number, string>();
  const converted = llmItems.map((item, i) => {
    const positioned = convertReportLLMItemToPositionedItem(
      item,
      primaryFilterId
    );
    if (positioned) {
      llmIndexToPositionedId.set(i, positioned.id);
    }
    return positioned;
  });
  const validatedItems = offsetToPage2(
    converted.filter((item): item is PositionedItem => item !== null)
  );
  const items = ensureContentOnPage2AndBeyond(validatedItems);
  return {
    items,
    gridRows: requiredGridRowsForItems(items),
    llmIndexToPositionedId,
  };
}
